import { Capacitor } from "@capacitor/core";

const LOCALHOST_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);
const isLocalhostUrl = (urlString: string) => {
  try {
    const url = new URL(urlString);
    return LOCALHOST_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
};

const normalizeBaseUrl = (urlString: string) => {
  try {
    const url = new URL(urlString);
    const pathname = url.pathname === "/" ? "" : url.pathname.replace(/\/+$/, "");
    return `${url.origin}${pathname}${url.search}${url.hash}`;
  } catch {
    return urlString.replace(/\/+$/, "");
  }
};

const mapLocalhostToNativeUrl = (
  urlString: string,
  nativeBaseUrl?: string
) => {
  try {
    const source = new URL(urlString);
    if (nativeBaseUrl) {
      const native = new URL(nativeBaseUrl);
      const port = native.port || source.port;
      const hostname = native.hostname;
      const protocol = native.protocol;
      const portSuffix = port ? `:${port}` : "";
      const origin = `${protocol}//${hostname}${portSuffix}`;
      const pathname =
        source.pathname === "/" ? "" : source.pathname.replace(/\/+$/, "");
      return normalizeBaseUrl(
        `${origin}${pathname}${source.search}${source.hash}`
      );
    }
    // No explicit native URL — derive host from the page origin (e.g. the
    // Mac's LAN IP that Capacitor is loading the dev server from).
    const pageHost = globalThis.location?.hostname;
    if (pageHost && !LOCALHOST_HOSTS.has(pageHost)) {
      const port = source.port;
      const portSuffix = port ? `:${port}` : "";
      const origin = `${source.protocol}//${pageHost}${portSuffix}`;
      const pathname =
        source.pathname === "/" ? "" : source.pathname.replace(/\/+$/, "");
      return normalizeBaseUrl(
        `${origin}${pathname}${source.search}${source.hash}`
      );
    }
    return normalizeBaseUrl(urlString);
  } catch {
    return normalizeBaseUrl(urlString);
  }
};

export const getRuntimeApiUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL as string;
  const nativeApiUrl = import.meta.env.VITE_NATIVE_API_URL as
    | string
    | undefined;

  if (
    Capacitor.isNativePlatform() &&
    apiUrl &&
    isLocalhostUrl(apiUrl)
  ) {
    return mapLocalhostToNativeUrl(apiUrl, nativeApiUrl);
  }

  return normalizeBaseUrl(apiUrl);
};

export const getRuntimeCmsApiUrl = () => {
  const cmsApiUrl = import.meta.env.VITE_CMS_API_URL as string;
  const nativeCmsApiUrl = import.meta.env.VITE_NATIVE_CMS_API_URL as
    | string
    | undefined;

  if (
    Capacitor.isNativePlatform() &&
    cmsApiUrl &&
    isLocalhostUrl(cmsApiUrl)
  ) {
    return mapLocalhostToNativeUrl(cmsApiUrl, nativeCmsApiUrl);
  }

  return normalizeBaseUrl(cmsApiUrl);
};
